import pluralize from 'pluralize';
import { formatKeywordForQuery } from './format-keyword';

import type { VideoCategory } from './stock-video-types';
import { VECTOR_SEARCH_DEFAULTS } from './vector-search';

/**
 * Derives the query-key-relevant parameters from a VideoCategory.
 *
 * Both SSR (getServerSideProps) and the client (Page component on first render)
 * MUST use this function to ensure identical React Query cache keys. If either
 * side computes these values differently, the SSR-prefetched data will be
 * discarded during hydration.
 *
 * On the client, the debug panel can override these values after the first
 * render via useEffect — but the initial render (which must match SSR) always
 * uses the category's stored values, which is what this function returns.
 */
export const getCategoryQueryDefaults = (
  category: Pick<
    VideoCategory,
    | 'name'
    | 'keywords'
    | 'embedding'
    | 'embedding_full'
    | 'use_exact_keyword_match'
    | 'use_full_embedding'
    | 'vector_distance_threshold'
  >
) => {
  const useFullEmbedding = category.use_full_embedding ?? false;
  const useExactKeywordMatch = category.use_exact_keyword_match ?? false;
  const distanceThreshold =
    category.vector_distance_threshold ??
    VECTOR_SEARCH_DEFAULTS.VIDEO.DISTANCE_THRESHOLD_DEFAULT;
  const embedding = useFullEmbedding
    ? category.embedding_full
    : category.embedding;

  // Derive keywords deterministically for SSR/client query key alignment.
  // Only fall back to pluralised category name when no keywords field exists
  // (undefined). An explicit empty array means the admin cleared keywords and
  // that decision should be respected.
  let keywords: string[];
  if (category.keywords == null) {
    const lower = category.name.toLowerCase();
    const singular = pluralize.singular(lower);
    const plural = pluralize.plural(lower);
    keywords = [...new Set([lower, singular, plural])];
  } else {
    keywords = category.keywords;
  }

  return {
    useFullEmbedding,
    useExactKeywordMatch,
    distanceThreshold,
    embedding,
    keywords,
  };
};

/**
 * Builds a Typesense filter_by clause requiring the video keywords array
 * to contain at least one of the given category keywords.
 * Used by both SSR and client for exact keyword match mode.
 */
const buildKeywordClause = (kw: string): string => {
  if (kw.includes('|')) {
    const andKeywords = kw.split('|').filter(Boolean);
    return `(${andKeywords.map(buildKeywordClause).join(' && ')})`;
  }

  const formattedKeyword = formatKeywordForQuery(kw)
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"');
  return `keywords:=["${formattedKeyword}"]`;
};

export const buildCategoryKeywordFilter = (keywords: string[]): string =>
  keywords.map(buildKeywordClause).join(' || ');

/**
 * What a category's listing searches with before any user filters. Keyword
 * categories filter on their keywords and skip the vector search; vector
 * categories search by embedding, widened by their keywords.
 */
export const getCategorySearchDefaults = (
  category: Parameters<typeof getCategoryQueryDefaults>[0]
) => {
  const {
    useFullEmbedding,
    useExactKeywordMatch,
    distanceThreshold,
    embedding,
    keywords,
  } = getCategoryQueryDefaults(category);
  const hasKeywords = keywords.length > 0;

  return {
    categoryEmbedding: useExactKeywordMatch ? undefined : embedding,
    useFullEmbedding: useExactKeywordMatch ? false : useFullEmbedding,
    distanceThreshold,
    filterBy:
      useExactKeywordMatch && hasKeywords
        ? buildCategoryKeywordFilter(keywords)
        : undefined,
    unionKeywords: !useExactKeywordMatch && hasKeywords ? keywords : undefined,
  };
};
